import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchDashboard } from '@/api/dashboard'
import { listAccounts as listAccountsApi } from '@/api/accounts'
import { listCategories } from '@/api/categories'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { WIDE_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist'
import { InstallAppCard } from '@/components/layout/InstallAppPrompt'
import { Button } from '@/components/ui/button'
import { MonthHeader } from '@/components/dashboard/MonthHeader'
import { NetWorthRow } from '@/components/dashboard/NetWorthRow'
import { MonthSummary } from '@/components/dashboard/MonthSummary'
import { SpendingDonut } from '@/components/dashboard/SpendingDonut'
import { DailySpendingStrip } from '@/components/dashboard/DailySpendingStrip'
import { BudgetPace } from '@/components/dashboard/BudgetPace'
import { SECTION_CLASS } from '@/components/dashboard/SectionLink'
import { monthProgress } from '@/components/dashboard/monthProgress'
import { AddTransactionModal } from '@/components/transactions/TransactionForms'
import { cn } from '@/lib/utils'

// Phone stacks the sections in reading order; desktop is a fixed three-row grid whose
// last row (the daily strip) absorbs leftover height, so the page fills the viewport and
// never scrolls however much data there is — every list on it is capped.
const GRID =
  "grid grid-cols-1 [grid-template-areas:'nw'_'install'_'month'_'spend'_'daily'_'budget'] " +
  'lg:flex-1 lg:min-h-0 lg:grid-cols-2 lg:gap-x-16 lg:grid-rows-[auto_auto_minmax(0,1fr)] ' +
  "lg:[grid-template-areas:'nw_month'_'spend_budget'_'daily_daily']"

function SkeletonSection({ area, tall }: { area: string; tall?: boolean }) {
  return (
    <div className={cn(SECTION_CLASS, area)} aria-hidden="true">
      <div className="animate-pulse">
        <div className="my-3 h-3 w-24 rounded bg-[var(--fl-surface-subtle)]" />
        <div className={cn('rounded bg-[var(--fl-surface-subtle)]', tall ? 'h-32' : 'h-12')} />
      </div>
    </div>
  )
}

export default function DashboardPage() {
  const { format: exact, formatCompact, formatWhole } = useFormatCurrency()
  // A phone Dashboard is a glance: whole rupees read faster and keep every figure on one line.
  const compact = !useMediaQuery(WIDE_QUERY)
  const format = compact ? formatWhole : exact
  const [addOpen, setAddOpen] = useState(false)
  const today = new Date()
  const progress = monthProgress(today)
  const monthName = today.toLocaleDateString('en-IN', { month: 'long' })

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['dashboard', 'THIS_MONTH'],
    queryFn: () => fetchDashboard('THIS_MONTH'),
    // The landing page should show current figures even inside the global staleTime,
    // e.g. right after adding a transaction elsewhere and navigating back.
    refetchOnMount: 'always',
  })
  // Same query keys as AppLayout's sidebar Add Transaction button, so the empty-state
  // CTA's modal reuses that cache rather than fetching again.
  const { data: accountsList } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => listAccountsApi(),
  })
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => listCategories(),
  })

  return (
    <div className="flex w-full flex-col px-4 py-4 md:px-10 lg:min-h-0 lg:flex-1 lg:py-7">
      <MonthHeader today={today} progress={progress} />
      <OnboardingChecklist />

      {isError && (
        <div className="mb-4 flex items-center justify-between rounded-xl border border-[var(--fl-error-dark)] bg-[var(--fl-error-bg)] p-4 text-[var(--fl-error-dark)]">
          <span>Couldn't load your dashboard.</span>
          <Button
            onClick={() => refetch()}
            variant="link"
            className="h-auto p-0 font-semibold underline text-[var(--fl-error-dark)]"
          >
            Retry
          </Button>
        </div>
      )}

      <div className={GRID}>
        {isPending ? (
          <>
            <SkeletonSection area="[grid-area:nw]" />
            <SkeletonSection area="[grid-area:month]" tall />
            <SkeletonSection area="[grid-area:spend]" tall />
            <SkeletonSection area="[grid-area:daily]" tall />
          </>
        ) : data ? (
          <>
            <NetWorthRow
              value={format(data.netWorth)}
              className={cn(SECTION_CLASS, '[grid-area:nw] motion-safe:animate-fade-slide-up')}
            />
            <InstallAppCard className="mb-3 [grid-area:install] lg:hidden" />
            <MonthSummary
              income={data.periodIncome}
              expenses={data.periodExpenses}
              savingsRate={data.savingsRate}
              format={format}
              className={cn(SECTION_CLASS, '[grid-area:month] motion-safe:animate-fade-slide-up')}
            />
            <SpendingDonut
              categories={data.spendingByCategory}
              monthName={monthName}
              format={format}
              compact={compact}
              onAddTransaction={() => setAddOpen(true)}
              className={cn(SECTION_CLASS, '[grid-area:spend] motion-safe:animate-fade-slide-up')}
            />
            <DailySpendingStrip
              daily={data.dailySpending ?? []}
              daysInMonth={progress.daysInMonth}
              today={progress.day}
              format={format}
              formatCompact={formatCompact}
              compact={compact}
              className={cn(SECTION_CLASS, '[grid-area:daily] motion-safe:animate-fade-slide-up')}
            />
          </>
        ) : null}
        <BudgetPace
          pacePercent={progress.pacePercent}
          format={format}
          className="[grid-area:budget] motion-safe:animate-fade-slide-up"
        />
      </div>

      {addOpen && (
        <AddTransactionModal
          accounts={accountsList?.accounts ?? []}
          categories={categoriesData?.categories ?? []}
          onClose={() => setAddOpen(false)}
        />
      )}
    </div>
  )
}
